/**
 * Pure tree helpers for the account Hierarchy tab. Shared so the server
 * (`resolveHierarchyParents`, which sets `parent_id` / `parent_missing`) and
 * the client (table + graph) agree on one definition of the tree.
 *
 * `hierarchy_level` is NOT used to build the tree (it is nullable and
 * mirror-only) — only `parent_account_id`, then `ultimate_global_parent_id`,
 * then the root as a last resort.
 */
import { HIERARCHY_OVERFLOW_CHILDREN, type AccountHierarchyNode } from './account-hierarchy';

/** What the SQL layer returns before parents are resolved. */
export type RawHierarchyNode = Omit<AccountHierarchyNode, 'parent_id' | 'parent_missing'>;

/** Child order: rank ASC NULLS LAST, ARR DESC NULLS LAST, name ASC (then id for determinism). */
export function compareHierarchyNodes(a: AccountHierarchyNode, b: AccountHierarchyNode): number {
  const rank = compareNullsLast(a.fortune_500_rank, b.fortune_500_rank, 1);
  if (rank !== 0) return rank;
  const arr = compareNullsLast(a.arr_active_subscriptions, b.arr_active_subscriptions, -1);
  if (arr !== 0) return arr;
  const name = (a.name ?? '').localeCompare(b.name ?? '');
  if (name !== 0) return name;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function compareNullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * dir;
}

/** The node names an SF parent that is not in the set (parent not mirrored / outside the cap). */
function hasMissingSfParent(n: RawHierarchyNode, ids: ReadonlySet<string>): boolean {
  return !!n.sf_parent_account_id && n.sf_parent_account_id !== n.id && !ids.has(n.sf_parent_account_id);
}

/**
 * Effective parent per node: `sf_parent_account_id` if it is in the set, else
 * `ultimate_global_parent_id` if in the set, else the root. `parent_missing` is set whenever
 * the node names an SF parent that is not in the set (the table renders
 * "(parent not in Terra)"). Each parent cycle is broken at its lowest-id member,
 * which re-attaches to the root (also flagged). The root itself gets `parent_id: null`; `parent_missing` is true on
 * it when it has an SF parent that is not in the set.
 */
export function resolveHierarchyParents(rawNodes: RawHierarchyNode[], rootId: string): AccountHierarchyNode[] {
  const ids = new Set(rawNodes.map(n => n.id));

  const proposed = new Map<string, { parent: string; missing: boolean }>();
  for (const n of rawNodes) {
    if (n.id === rootId) continue;
    if (n.sf_parent_account_id && n.sf_parent_account_id !== n.id && ids.has(n.sf_parent_account_id)) {
      proposed.set(n.id, { parent: n.sf_parent_account_id, missing: false });
    } else if (n.ultimate_global_parent_id && n.ultimate_global_parent_id !== n.id && ids.has(n.ultimate_global_parent_id)) {
      proposed.set(n.id, { parent: n.ultimate_global_parent_id, missing: hasMissingSfParent(n, ids) });
    } else {
      proposed.set(n.id, { parent: rootId, missing: hasMissingSfParent(n, ids) });
    }
  }
  // Cycle repair: break each loop at its lowest-id member (re-attach it to the
  // root) and leave everything else alone, so nodes merely hanging off a loop
  // (C -> A, D -> C) stay attached to their own parent. Each break is applied
  // immediately so later walks reach the root.
  const sortedIds = rawNodes.map(n => n.id).filter(id => id !== rootId).sort();
  for (const start of sortedIds) {
    const order: string[] = [];
    let cursor: string | undefined = start;
    while (cursor !== undefined && cursor !== rootId) {
      if (order.includes(cursor)) {
        const loop = order.slice(order.indexOf(cursor));
        const lowest = loop.reduce((m, id) => (id < m ? id : m));
        proposed.set(lowest, { parent: rootId, missing: true });
        break;
      }
      order.push(cursor);
      cursor = proposed.get(cursor)?.parent;
    }
  }

  return rawNodes.map(n => {
    if (n.id === rootId) return { ...n, parent_id: null, parent_missing: hasMissingSfParent(n, ids) };
    const p = proposed.get(n.id)!;
    return { ...n, parent_id: p.parent, parent_missing: p.missing };
  });
}

export interface SubtreeRollup {
  /** Accounts below this node (excluding itself). */
  accountCount: number;
  directCount: number;
}

export interface HierarchyTree {
  rootId: string;
  byId: Map<string, AccountHierarchyNode>;
  /** Children sorted by `compareHierarchyNodes`. */
  childrenOf: Map<string, AccountHierarchyNode[]>;
  depthOf: Map<string, number>;
  /** Ancestor ids from the node's parent up to the root (nearest first). */
  ancestorsOf: (id: string) => string[];
  /** Account counts beneath a node (no metric roll-ups: the UI shows each account's own values). */
  subtreeRollup: (id: string) => SubtreeRollup;
}

export function buildHierarchyTree(nodes: AccountHierarchyNode[], rootId: string): HierarchyTree {
  const byId = new Map(nodes.map(n => [n.id, n]));
  const childrenOf = new Map<string, AccountHierarchyNode[]>();
  for (const n of nodes) {
    if (n.parent_id === null || n.id === rootId) continue;
    const list = childrenOf.get(n.parent_id);
    if (list) list.push(n); else childrenOf.set(n.parent_id, [n]);
  }
  for (const list of childrenOf.values()) list.sort(compareHierarchyNodes);

  const depthOf = new Map<string, number>();
  const stack: Array<[string, number]> = [[rootId, 0]];
  while (stack.length) {
    const [id, depth] = stack.pop()!;
    if (depthOf.has(id)) continue;
    depthOf.set(id, depth);
    for (const c of childrenOf.get(id) ?? []) stack.push([c.id, depth + 1]);
  }

  const ancestorsOf = (id: string): string[] => {
    const out: string[] = [];
    let cursor = byId.get(id)?.parent_id ?? null;
    while (cursor !== null && !out.includes(cursor)) {
      out.push(cursor);
      cursor = byId.get(cursor)?.parent_id ?? null;
    }
    return out;
  };

  const rollups = new Map<string, SubtreeRollup>();
  const subtreeRollup = (id: string): SubtreeRollup => {
    const cached = rollups.get(id);
    if (cached) return cached;
    const kids = childrenOf.get(id) ?? [];
    const total: SubtreeRollup = { accountCount: 0, directCount: kids.length };
    for (const k of kids) total.accountCount += 1 + subtreeRollup(k.id).accountCount;
    rollups.set(id, total);
    return total;
  };

  return { rootId, byId, childrenOf, depthOf, ancestorsOf, subtreeRollup };
}

/** Above this many nodes a fully expanded tree is unreadable, so only the top levels open. */
export const HIERARCHY_FULL_EXPAND_MAX_NODES = 120;
/** For larger trees: nodes shallower than this depth are expanded (the root is depth 0). */
export const HIERARCHY_LARGE_TREE_EXPAND_DEPTH = 3;

/**
 * Initially expanded ids: every node that has children (the whole tree open).
 * A tree beyond `HIERARCHY_FULL_EXPAND_MAX_NODES` nodes would be unreadable fully open, so
 * only nodes above depth `HIERARCHY_LARGE_TREE_EXPAND_DEPTH` expand (plus the current account's
 * ancestors, so it is always visible). A parent's >12-children
 * overflow ("+N accounts") still applies on top via `visibleChildren`,, which also keeps the
 * current account's ancestor path in the shown set.
 */
export function defaultExpandedIds(tree: HierarchyTree, currentId: string): Set<string> {
  const limitDepth = tree.byId.size > HIERARCHY_FULL_EXPAND_MAX_NODES;
  const out = new Set<string>();
  for (const [id, kids] of tree.childrenOf) {
    if (kids.length === 0) continue;
    if (limitDepth && (tree.depthOf.get(id) ?? Infinity) >= HIERARCHY_LARGE_TREE_EXPAND_DEPTH) continue;
    out.add(id);
  }
  if (limitDepth) for (const a of tree.ancestorsOf(currentId)) out.add(a);
  return out;
}

export interface VisibleChildren {
  shown: AccountHierarchyNode[];
  hiddenCount: number;
}

/**
 * Children to render under `parentId`. At most `HIERARCHY_OVERFLOW_CHILDREN`
 * unless the parent is in `revealedIds`; the current account's ancestor path
 * is always kept in the shown set (replacing the tail of the first N).
 */
export function visibleChildren(
  tree: HierarchyTree,
  parentId: string,
  currentId: string,
  revealedIds: ReadonlySet<string>,
): VisibleChildren {
  const kids = tree.childrenOf.get(parentId) ?? [];
  if (kids.length <= HIERARCHY_OVERFLOW_CHILDREN || revealedIds.has(parentId)) {
    return { shown: kids, hiddenCount: 0 };
  }
  const pathIds = new Set([currentId, ...tree.ancestorsOf(currentId)]);
  const mustKeep = kids.filter(k => pathIds.has(k.id));
  const keepIds = new Set(mustKeep.map(k => k.id));
  const chosen = new Set(keepIds);
  for (const k of kids) {
    if (chosen.size >= HIERARCHY_OVERFLOW_CHILDREN) break;
    chosen.add(k.id);
  }
  const shown = kids.filter(k => chosen.has(k.id));
  return { shown, hiddenCount: kids.length - shown.length };
}

export type HierarchyRow =
  | { kind: 'node'; node: AccountHierarchyNode; depth: number; hasChildren: boolean; expanded: boolean }
  | { kind: 'more'; parentId: string; depth: number; hiddenCount: number };

/** Depth-first rows for the table: expanded nodes show their (overflow-capped) children. */
export function flattenVisibleRows(
  tree: HierarchyTree,
  expandedIds: ReadonlySet<string>,
  revealedIds: ReadonlySet<string>,
  currentId: string,
): HierarchyRow[] {
  const rows: HierarchyRow[] = [];
  const root = tree.byId.get(tree.rootId);
  if (!root) return rows;
  const visit = (node: AccountHierarchyNode, depth: number): void => {
    const hasChildren = (tree.childrenOf.get(node.id)?.length ?? 0) > 0;
    const expanded = hasChildren && expandedIds.has(node.id);
    rows.push({ kind: 'node', node, depth, hasChildren, expanded });
    if (!expanded) return;
    const { shown, hiddenCount } = visibleChildren(tree, node.id, currentId, revealedIds);
    for (const c of shown) visit(c, depth + 1);
    if (hiddenCount > 0) rows.push({ kind: 'more', parentId: node.id, depth: depth + 1, hiddenCount });
  };
  visit(root, 0);
  return rows;
}

export interface HierarchyNeighbors {
  /** Effective parent (null for the top of the tree, or when `parentMissing`). */
  parent: AccountHierarchyNode | null;
  /** The SF parent is not in Terra, so there is nothing to link. */
  parentMissing: boolean;
  /** Direct children, in tree order (`compareHierarchyNodes`). */
  children: AccountHierarchyNode[];
}

/**
 * The current account's parent and children as the Hierarchy tab resolves them (same
 * `parent_id` the table/graph use), for the drilldown's field panel. Null when there is no
 * hierarchy to show: no nodes, the account is not in the set, or it is alone with no missing parent.
 */
export function hierarchyNeighbors(nodes: AccountHierarchyNode[], rootId: string, currentId: string): HierarchyNeighbors | null {
  const tree = buildHierarchyTree(nodes, rootId);
  const self = tree.byId.get(currentId);
  if (!self) return null;
  const children = tree.childrenOf.get(currentId) ?? [];
  if (nodes.length === 1 && !self.parent_missing) return null;
  return {
    parent: self.parent_missing || self.parent_id === null ? null : (tree.byId.get(self.parent_id) ?? null),
    parentMissing: self.parent_missing,
    children,
  };
}

/**
 * A copy of `tree` whose sibling groups are reordered by `compare` (ties keep the default order).
 * Only siblings are reordered: a node never leaves its parent, so the table keeps its tree
 * grouping while sorting.
 */
export function withSortedChildren(
  tree: HierarchyTree,
  compare: (a: AccountHierarchyNode, b: AccountHierarchyNode) => number,
): HierarchyTree {
  const childrenOf = new Map<string, AccountHierarchyNode[]>();
  for (const [id, kids] of tree.childrenOf) {
    childrenOf.set(id, [...kids].sort((a, b) => compare(a, b) || compareHierarchyNodes(a, b)));
  }
  return { ...tree, childrenOf };
}

/**
 * "Show only matching": drop table rows that no active lens highlights, keeping every ancestor of
 * any match (dimmed by the caller) so indentation still reads as a tree. `null` = no lens active =
 * every row stays. "+N more" rows go with the filter (they describe hidden children).
 */
export function filterRowsToMatching(
  rows: HierarchyRow[],
  highlightedIds: ReadonlySet<string> | null,
  tree: HierarchyTree,
): HierarchyRow[] {
  if (highlightedIds === null) return rows;
  // Ancestors come from EVERY match in the tree, not just the visible rows: a collapsed branch whose
  // child matches must keep its row (and its expand control).
  const keep = new Set<string>();
  for (const id of highlightedIds) {
    if (!tree.byId.has(id)) continue;
    keep.add(id);
    for (const a of tree.ancestorsOf(id)) keep.add(a);
  }
  return rows.filter(row => row.kind === 'node' && keep.has(row.node.id));
}
